import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, clearToken, getToken, setToken } from "@/lib/api";
import { applyRTL, loadLang, saveLang, getQueryLang, hasPickedLanguage } from "@/lib/i18n";

const Ctx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLangState] = useState("en");

  useEffect(() => {
    (async () => {
      // Search deep-links like /?lang=es implicitly pick that language so the
      // first-launch language gate does not block visitors arriving in their
      // own language from a search result.
      const ql = getQueryLang();
      if (ql && !hasPickedLanguage()) saveLang(ql);
      const l = loadLang();
      setLangState(l);
      applyRTL(l);
      const token = getToken();
      if (token) {
        try {
          const u = await api.me();
          setUser(u);
        } catch {
          clearToken();
        }
      }
      setLoading(false);
    })();
  }, []);

  const setLang = useCallback(
    async (l) => {
      setLangState(l);
      saveLang(l);
      applyRTL(l);
      if (getToken()) {
        try {
          await api.updateSettings({ language: l });
        } catch {}
      }
    },
    []
  );

  const signIn = useCallback(async (email, password) => {
    const r = await api.login(email, password);
    setToken(r.access_token);
    setUser(r.user);
  }, []);

  const signUp = useCallback(async (email, password, full_name) => {
    const r = await api.register(email, password, full_name);
    setToken(r.access_token);
    setUser(r.user);
  }, []);

  const signInAsGuest = useCallback(async () => {
    const r = await api.guestSignup();
    setToken(r.access_token);
    setUser(r.user);
  }, []);

  const signOut = useCallback(async () => {
    clearToken();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const u = await api.me();
      setUser(u);
    } catch {}
  }, []);

  return (
    <Ctx.Provider
      value={{ user, loading, lang, setLang, signIn, signUp, signInAsGuest, signOut, refreshUser }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
